// WHAT: React Provider component for sidebar state management
// WHY:  Provides sidebar state and controls to child components via context
// DOMAIN: core → layout
// DEPENDENCIES: SidebarContext from sidebarContext.ts, React useState
// RISKS:
//   - NOTE: Der Zustand ist nur im Frontend gespeichert; ein Reload setzt die Sidebar wieder auf den Default-State
import { useState, type ReactNode } from "react";

import { SidebarContext } from "./SidebarContext.shared";

interface SidebarProviderProperties {
  children: ReactNode;
}

/**
 * WHAT:
 *   Context-Provider, der den Sidebar-Sichtbarkeitszustand (`isOpen`) und zugehörige
 *   Steuerfunktionen (`toggle`, `open`, `close`) kapselt.
 *
 * WHY:
 *   Ermöglicht es, Sidebar-Interaktionen (z. B. in Header, Navigation, Content) ohne
 *   Prop-Drilling konsistent zu implementieren.
 *
 * BEHAVIOR:
 *   - Initialisiert `isOpen` standardmäßig mit `true` (Sidebar offen).
 *   - Stellt einen `SidebarContext` bereit, auf den via `useSidebar` zugegriffen wird.
 *
 * RISKS:
 *   - NOTE: Der Hook `useSidebar` wirft bewusst einen Fehler, wenn er außerhalb des Providers verwendet wird.
 *
 * @param root0 - Component props
 *
 * @param root0.children - Child components
 *
 * @returns JSX element with SidebarContext provider
 *
 */
export const SidebarProvider = ({ children }: SidebarProviderProperties) => {
  const [isOpen, setIsOpen] = useState(true); // Default to open

  const toggle = () => {
    setIsOpen(!isOpen);
  };
  const open = () => {
    setIsOpen(true);
  };
  const close = () => {
    setIsOpen(false);
  };

  return (
    <SidebarContext.Provider value={{ close, isOpen, open, toggle }}>
      {children}
    </SidebarContext.Provider>
  );
};
